import { t } from '@lingui/core/macro'
import { useLingui } from '@lingui/react/macro'
import { useGetRolodexEntriesByLoadOptions } from 'features/rolodex'
import {
  contextIdFromEntryId,
  isListableSentinel,
  nextStalledTransition,
  resolveRunState,
  sentinelPollInterval,
  type SentinelRunState,
  type SentinelRunTiming,
} from 'features/sentinels/runState'
import {
  SENTINEL_ENTRY_TYPE,
  SentinelLoadedEntry,
  SentinelMode,
  SentinelProgress,
  SentinelRunFailure,
  SentinelRunStatus,
} from 'features/sentinels/types'
import { useRunStateClock } from 'features/sentinels/useRunStateClock'
import { useCallback, useMemo } from 'react'

export interface SentinelSummary {
  id: string
  name: string
  /** One-line description of what the sentinel covers. */
  detail: string
  status: SentinelRunStatus
  /** `status`, with `running` narrowed to `stalled` once it has run too long. */
  runState: SentinelRunState
  updatedAt: string
  /** When the run was requested; the clock the stalled rule reads. */
  requestedAt?: string
  /** Why the run failed, when it did. */
  error?: string
  /** The same failure with its stage and row counts, when the job named them. */
  failure?: SentinelRunFailure
  /** The job's latest reported stage, while one is in flight. */
  progress?: SentinelProgress
  mode?: SentinelMode
  /** Datapoint entry id of the target tag (target-mode sentinels only). */
  target?: string
  monitorEnabled: boolean
  /** The design-context bundle this run was planned from. */
  contextEntryId?: string
  /** The asset instance this sentinel monitors, from the entry's tags. */
  instanceEntryId?: string
  /** Path of the entry's result.json file (set once training is done). */
  resultPath?: string
  /** Training window start (YYYY-MM-DD) — for framing the chart on add. */
  windowStart?: string
  /** Training window end (YYYY-MM-DD) — the test→forecast boundary. */
  windowEnd?: string
  /** The HONEST train→test split date from the design fit (not the served
   * refit's, which is the training end). */
  splitDate?: string
}

const timingOf = (row: SentinelLoadedEntry): SentinelRunTiming => ({
  status: row.entry.data.summary?.status,
  requestedAt: row.entry.data.summary?.requested_at,
  updatedAt: row.entry.updated_at,
})

/** The listable rows, in the order they arrived. */
const listableRows = (
  rows: readonly SentinelLoadedEntry[] | undefined
): SentinelLoadedEntry[] =>
  (rows ?? []).filter((row) =>
    isListableSentinel({
      id: String(row.entry.id || ''),
      mode: row.entry.data.summary?.mode,
    })
  )

/**
 * Exported for the poll-interval callback and for tests: SWR hands the callback
 * raw rows, and it has to reach the same verdict the render does.
 */
export const toSentinelSummaries = (
  rows: readonly SentinelLoadedEntry[] | undefined,
  now: number = Date.now()
): SentinelSummary[] =>
  listableRows(rows)
    .map((row) => {
      const d = row.entry.data
      const summary = d.summary
      const detail =
        summary?.mode === 'target'
          ? t`${{ value90_17: summary.target ?? 'target' }} · ${{ value90_49: summary.drivers ?? '?' }} drivers`
          : t`${{ value91_17: summary?.tags ?? '?' }} tags in ${{ value91_49: summary?.families ?? '?' }} families`
      return {
        id: row.entry.id,
        name: d.name || row.entry.id,
        detail,
        status: summary?.status ?? 'done',
        runState: resolveRunState(timingOf(row), now),
        updatedAt: row.entry.updated_at,
        requestedAt: summary?.requested_at,
        error: summary?.error,
        failure: summary?.failure,
        progress: summary?.progress,
        mode: summary?.mode,
        target: summary?.target,
        monitorEnabled: d.monitor?.enabled === true,
        contextEntryId: summary?.context_entry_id,
        instanceEntryId: row.entry.tags?.instance,
        resultPath: d.files?.result,
        windowStart: summary?.start_date,
        windowEnd: summary?.end_date,
        splitDate: summary?.split_date,
      }
    })
    .sort((a, b) => a.name.localeCompare(b.name))

/**
 * A design context that was started but never produced a run — "unfinished".
 *
 * You build a context (and maybe design a plan), leave, and come back: the
 * `fim-sentinel-dctx-…` entry is already in the list query (it carries the
 * `fim-sentinel` type and the site tag) and is only dropped by
 * `isListableSentinel`. Surfacing it as a grayed, resumable item is a matter of
 * keeping it, not a new query.
 */
export interface UnfinishedDesign {
  /** The design-context entry id. */
  entryId: string
  /** The 16-hex context id, for resuming via `?designContextId=`. */
  contextId: string
  name: string
  /** How far the design got. */
  stage: 'building' | 'needs-design' | 'needs-run'
  /** One-line description of where it was left. */
  detail: string
  updatedAt: string
}

const DCTX_ID_PREFIX = 'fim-sentinel-dctx-'

const isDesignContextRow = (row: SentinelLoadedEntry): boolean =>
  row.entry.data.summary?.mode === 'design_context' ||
  String(row.entry.id || '').startsWith(DCTX_ID_PREFIX)

/**
 * The design contexts with no run yet, newest first. A context is "finished"
 * (and so omitted) the moment ANY run — even a failed one — references it, since
 * that run then lives in the main list with its own state and re-run path. Only
 * contexts whose id can be read back (so they can be resumed) are kept.
 *
 * Exported for tests and the poll callback, mirroring `toSentinelSummaries`.
 */
export const toUnfinishedDesigns = (
  rows: readonly SentinelLoadedEntry[] | undefined,
  runs: readonly SentinelSummary[]
): UnfinishedDesign[] => {
  const contextsWithRun = new Set(
    runs.map((run) => run.contextEntryId).filter(Boolean)
  )
  return (rows ?? [])
    .filter(isDesignContextRow)
    .filter((row) => !contextsWithRun.has(String(row.entry.id)))
    .map((row): UnfinishedDesign | null => {
      const contextId = contextIdFromEntryId(String(row.entry.id))
      if (!contextId) return null
      const d = row.entry.data
      // A context whose build errored or stalled (the job died) can never be
      // resumed — it would only ever read "building…" — so drop it rather than
      // dangle a dead-end in the sidebar.
      const runState = resolveRunState(timingOf(row))
      if (runState === 'error' || runState === 'stalled') return null
      const designed = d.summary?.has_plan ?? false
      let stage: UnfinishedDesign['stage']
      let detail: string
      if (runState === 'running') {
        stage = 'building'
        detail = t`building context…`
      } else if (designed) {
        stage = 'needs-run'
        detail = t`designed for ${{ value179_35: d.summary?.plan_target ?? 'a target' }} · not run`
      } else {
        stage = 'needs-design'
        detail = t`context built · not designed`
      }
      return {
        entryId: String(row.entry.id),
        contextId,
        name: d.name || String(row.entry.id),
        stage,
        detail,
        updatedAt: row.entry.updated_at,
      }
    })
    .filter((design): design is UnfinishedDesign => design !== null)
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
}

/**
 * The newest run of one plan: one design context, one target.
 *
 * A run's entry id is generated per run by fim-service and cannot be computed
 * ahead of time — deliberately, since that is what stopped a second run from
 * overwriting the first one's record. So the design flow recognises the run it
 * has already started the way the service's own double-submit guard does, by the
 * pair that identifies the plan. Anything else about the run (a second target
 * off the same bundle, a run of another asset) is a different sentinel and is
 * none of this flow's business.
 */
export const findRunOfPlan = (
  sentinels: readonly SentinelSummary[],
  contextEntryId: string | undefined,
  target: string | undefined
): SentinelSummary | undefined => {
  if (!contextEntryId || !target) return undefined
  const startedAt = (run: SentinelSummary) => run.requestedAt ?? run.updatedAt
  return sentinels
    .filter(
      (run) => run.contextEntryId === contextEntryId && run.target === target
    )
    .sort((a, b) => startedAt(b).localeCompare(startedAt(a)))[0]
}

/**
 * How long to wait between re-reads while a just-started run is still absent
 * from the list, in milliseconds.
 *
 * A single read is not enough. Rolodex acknowledges the write that creates the
 * run's entry before its own list query can see that entry, so a revalidation
 * fired the instant a run starts can legitimately come back without the new row
 * — which on screen is indistinguishable from not having revalidated at all.
 *
 * The schedule gives up after about twenty seconds, on purpose. A row that has
 * not landed by then will not be rescued by a tighter loop, the poll interval
 * takes over the moment it does land, and SWR revalidates on focus regardless.
 * Nothing here is allowed to run forever: this window exists only because the
 * list has no reason to poll until it can already see something running.
 */
const NEW_RUN_SETTLE_DELAYS_MS: readonly number[] = [
  0, 750, 1_500, 3_000, 5_000, 10_000,
]

/**
 * List all sentinel entries for a site. Each create is a new dated entry;
 * delete from the UI rather than overwriting.
 *
 * The query re-polls while any of those runs has a job in flight, which is how a
 * run started in another tab — or in this one before a refresh — shows up at all.
 * fim-service's progress stream cannot be re-attached, but it only ever relayed
 * this entry's own fields, so re-reading them loses nothing but the scrollback.
 */
export const useSentinelList = (siteId: string | null) => {
  const { i18n } = useLingui()
  const { data, mutate, ...rest } = useGetRolodexEntriesByLoadOptions<
    SentinelLoadedEntry[]
  >(
    siteId
      ? {
          type: [SENTINEL_ENTRY_TYPE],
          tags: { site: siteId },
          limit: 200,
        }
      : undefined,
    {
      refreshInterval: (latest) =>
        sentinelPollInterval(
          toSentinelSummaries(latest).map((sentinel) => sentinel.runState)
        ),
      // A hidden tab has nobody watching the run, and SWR revalidates on focus,
      // so the poll waits for someone to look. This is SWR's default; it is
      // stated because the whole point of the interval above is to be cheap.
      refreshWhenHidden: false,
    }
  )

  const transitionAt = useMemo(
    () => nextStalledTransition(listableRows(data).map(timingOf)),
    [data]
  )
  const now = useRunStateClock(transitionAt)

  const sentinels = useMemo(
    () => toSentinelSummaries(data, now),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Summary details are localized by the mapper.
    [data, now, i18n.locale]
  )

  const unfinishedDesigns = useMemo(
    () => toUnfinishedDesigns(data, sentinels),
    [data, sentinels]
  )

  /**
   * Bring a run that was started moments ago into the list, and answer whether
   * it arrived.
   *
   * Every consumer of this hook shares one SWR key, so revalidating here puts
   * the row in the sidebar, the pill and the create flow at once — no matter
   * which of them asked. Deliberately NOT tied to the caller's lifetime: the
   * flow that starts a run is routinely unmounted by the navigation that
   * follows, and the row still has to appear for whoever is left watching.
   */
  const awaitNewRun = useCallback(
    async (entryId: string): Promise<boolean> => {
      for (const delay of NEW_RUN_SETTLE_DELAYS_MS) {
        if (delay > 0) {
          await new Promise((resolve) => window.setTimeout(resolve, delay))
        }
        const rows = await mutate()
        if (rows?.some((row) => row.entry.id === entryId)) return true
      }
      return false
    },
    [mutate]
  )

  return { sentinels, unfinishedDesigns, mutate, awaitNewRun, ...rest }
}

/**
 * The runs this site currently has jobs for, newest request first.
 *
 * Deliberately excludes stalled runs: they stay visible in the list with their
 * own label, but nothing should follow them around the page.
 */
export const useRunningSentinels = (siteId: string | null) => {
  const { sentinels, ...rest } = useSentinelList(siteId)
  const running = useMemo(
    () =>
      sentinels
        .filter((sentinel) => sentinel.runState === 'running')
        .sort((a, b) =>
          (b.requestedAt ?? '').localeCompare(a.requestedAt ?? '')
        ),
    [sentinels]
  )
  return { running, sentinels, ...rest }
}
